import { useState } from "react";
import {
  approveRecurringPass,
  createRecurringPass,
  listRecurringPasses,
  revokeRecurringPass,
  type RecurringPass,
  type RecurringPassCreatePayload,
} from "./api";
import { listHostsDirectory } from "../dashboard/api";
import { useAdminResource } from "./use-admin-resource";
import { useToast } from "../../components/useToast";

type PassType = RecurringPassCreatePayload["pass_type"];

type FormState = {
  fullName: string;
  hostId: string;
  purpose: string;
  passType: PassType;
  validFrom: string;
  validUntil: string;
  phone: string;
  email: string;
};

const INITIAL_FORM: FormState = {
  fullName: "",
  hostId: "",
  purpose: "",
  passType: "monthly",
  validFrom: "",
  validUntil: "",
  phone: "",
  email: "",
};

function validity(pass: RecurringPass): string {
  if (pass.pass_type === "permanent") return `From ${pass.valid_from} · no expiry`;
  return `${pass.valid_from} → ${pass.valid_until ?? "—"}`;
}

export function RecurringPassesPanel() {
  const { showSuccess, showError } = useToast();
  const { data: hosts } = useAdminResource(() => listHostsDirectory(), []);
  const { data: passes, refresh } = useAdminResource(() => listRecurringPasses(), []);

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [pending, setPending] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const pendingRequests = (passes ?? []).filter((p) => p.status === "PENDING");
  const activePasses = (passes ?? []).filter((p) => p.status !== "PENDING");

  async function submitCreate() {
    if (!form.fullName.trim()) return showError("Visitor name is required");
    const hostId = Number(form.hostId);
    if (!hostId) return showError("Select a host for this pass");
    if (!form.purpose.trim()) return showError("Purpose is required");
    if (form.passType === "custom" && (!form.validFrom || !form.validUntil)) {
      return showError("A custom pass needs a start and end date");
    }
    setPending(true);
    try {
      await createRecurringPass({
        full_name: form.fullName.trim(),
        host_id: hostId,
        purpose: form.purpose.trim(),
        pass_type: form.passType,
        valid_from: form.validFrom || undefined,
        valid_until: form.passType === "custom" ? form.validUntil || undefined : undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
      });
      showSuccess("Reusable pass issued");
      setForm(INITIAL_FORM);
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to issue pass");
    } finally {
      setPending(false);
    }
  }

  async function act(passId: number, action: "approve" | "revoke") {
    setBusyId(passId);
    try {
      if (action === "approve") await approveRecurringPass(passId);
      else await revokeRecurringPass(passId);
      showSuccess(action === "approve" ? "Pass approved" : "Pass revoked");
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Action failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Long-term Passes</h2>
        <span>Reusable badges</span>
      </header>
      <p className="management-note">
        Issue one reusable badge valid for a whole period (monthly, yearly, permanent, or a custom
        date range). The holder scans it to enter on any day within the window — no re-approval each
        visit. Hosts can also request a pass; those appear below for you to approve.
      </p>

      <form
        className="action-form action-card"
        onSubmit={(event) => {
          event.preventDefault();
          void submitCreate();
        }}
      >
        <div className="checkin-fields">
          <input
            value={form.fullName}
            onChange={(event) => setForm((p) => ({ ...p, fullName: event.target.value }))}
            placeholder="Visitor / holder name *"
          />
          <select
            className="search-input"
            value={form.hostId}
            onChange={(event) => setForm((p) => ({ ...p, hostId: event.target.value }))}
            aria-label="Host"
          >
            <option value="">Select host *</option>
            {(hosts ?? []).map((host) => (
              <option key={host.id} value={host.id}>
                {host.name}
              </option>
            ))}
          </select>
          <input
            value={form.purpose}
            onChange={(event) => setForm((p) => ({ ...p, purpose: event.target.value }))}
            placeholder="Purpose *"
          />
          <select
            className="search-input"
            value={form.passType}
            onChange={(event) => setForm((p) => ({ ...p, passType: event.target.value as PassType }))}
            aria-label="Pass duration"
          >
            <option value="monthly">Monthly (1 month)</option>
            <option value="yearly">Yearly (1 year)</option>
            <option value="permanent">Permanent (no expiry)</option>
            <option value="custom">Custom date range</option>
          </select>
          <input
            type="date"
            value={form.validFrom}
            onChange={(event) => setForm((p) => ({ ...p, validFrom: event.target.value }))}
            aria-label="Valid from (defaults to today)"
            title="Valid from (defaults to today)"
          />
          {form.passType === "custom" ? (
            <input
              type="date"
              value={form.validUntil}
              onChange={(event) => setForm((p) => ({ ...p, validUntil: event.target.value }))}
              aria-label="Valid until"
              title="Valid until"
            />
          ) : null}
          <input
            value={form.phone}
            onChange={(event) => setForm((p) => ({ ...p, phone: event.target.value }))}
            placeholder="Phone (optional)"
          />
          <input
            type="email"
            value={form.email}
            onChange={(event) => setForm((p) => ({ ...p, email: event.target.value }))}
            placeholder="Email (optional)"
          />
        </div>
        <button className="refresh-btn action-submit" type="submit" disabled={pending}>
          {pending ? "Issuing…" : "Issue Reusable Pass"}
        </button>
      </form>

      {pendingRequests.length > 0 ? (
        <div className="tool-card">
          <h3 style={{ margin: "0 0 0.5rem" }}>Host requests awaiting approval</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Holder</th>
                  <th>Host</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                  <th>Validity</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((p) => (
                  <tr key={p.id}>
                    <td>{p.visitor_name}</td>
                    <td>{p.host_name ?? "—"}</td>
                    <td>{p.purpose}</td>
                    <td>{p.pass_type}</td>
                    <td>{validity(p)}</td>
                    <td className="action-cell">
                      <button
                        type="button"
                        className="refresh-btn"
                        disabled={busyId === p.id}
                        onClick={() => void act(p.id, "approve")}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="secondary-btn"
                        disabled={busyId === p.id}
                        onClick={() => void act(p.id, "revoke")}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="table-wrap" style={{ marginTop: "1rem" }}>
        <table>
          <thead>
            <tr>
              <th>Holder</th>
              <th>Host</th>
              <th>Duration</th>
              <th>Validity</th>
              <th>Status</th>
              <th>Pass link</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {activePasses.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-cell">
                  No reusable passes issued yet.
                </td>
              </tr>
            ) : (
              activePasses.map((p) => (
                <tr key={p.id}>
                  <td>{p.visitor_name}</td>
                  <td>{p.host_name ?? "—"}</td>
                  <td>{p.pass_type}</td>
                  <td>{validity(p)}</td>
                  <td>{p.status}</td>
                  <td>
                    {p.pass_url ? (
                      <a href={p.pass_url} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {p.status === "ACTIVE" ? (
                      <button
                        type="button"
                        className="secondary-btn"
                        disabled={busyId === p.id}
                        onClick={() => void act(p.id, "revoke")}
                      >
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </article>
  );
}
